import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import RoleDashboard from "./pages/RoleDashboard";
import ApprovalsCenter from "./pages/ApprovalsCenter";
import HelpSupport from "./pages/HelpSupport";
import Products from "./pages/Products";
import ProductCatalogue from "./pages/ProductCatalogue";
import AdminCatalogue from "./pages/AdminCatalogue";
import DealerRegister from "./pages/DealerRegister";
import AdminDealers from "./pages/AdminDealers";
import Campaigns from "./pages/Campaigns";
import Leads from "./pages/Leads";
import LeadDetailPage from "./pages/LeadDetailPage";
import DealDetailPage from "./pages/DealDetailPage";
import BillingDocPage from "./pages/BillingDocPage";
import SocialMedia from "./pages/SocialMedia";
import EmailMarketing from "./pages/EmailMarketing";
import Analytics from "./pages/Analytics";
import CRMDashboard from "./pages/CRMDashboard";
import CRMContacts from "./pages/CRMContacts";
import CRMDeals from "./pages/CRMDeals";
import CRMTasks from "./pages/CRMTasks";
import CRMActivities from "./pages/CRMActivities";
import ProductDetail from "./pages/ProductDetail";
import Reports from "./pages/Reports";
import Forecasting from "./pages/Forecasting";
import Contact360 from "./pages/Contact360";
import TeamPerformance from "./pages/TeamPerformance";
import LeadScoring from "./pages/LeadScoring";
import Meetings from "./pages/Meetings";
import SalesFunnel from "./pages/SalesFunnel";
import SocialLeads from "./pages/SocialLeads";
import UserManagement from "./pages/UserManagement";
import PromotionsManager from "./pages/PromotionsManager";
import WhatsAppHub from "./pages/WhatsAppHub";
import PublicQuote from "./pages/PublicQuote";
import BillingCenter from "./pages/BillingCenter";
import IncentiveCenter from "./pages/IncentiveCenter";
import IncentiveSRS from "./pages/IncentiveSRS";
import Notes from "./pages/Notes";
import Alerts from "./pages/Alerts";
import AdminEnquiries from "./pages/AdminEnquiries";
import AdminProductLeads from "./pages/AdminProductLeads";
import LeadsDashboard from "./pages/LeadsDashboard";
import ReportsHub from "./pages/ReportsHub";
import PipelineVelocityReport from "./pages/reports/PipelineVelocityReport";
import BDEWorkloadReport from "./pages/reports/BDEWorkloadReport";
import PublicPdfEngagementReport from "./pages/reports/PublicPdfEngagementReport";
import AdminSettings from "./pages/AdminSettings";
import MarketingCampaigns from "./pages/MarketingCampaigns";
import SocialHub from "./pages/SocialHub";
import BDEDashboard from "./pages/BDEDashboard";
import ManagerDashboard from "./pages/ManagerDashboard";
import AdminDepartments from "./pages/AdminDepartments";
import AdminRoles from "./pages/AdminRoles";
import MDDashboard from "./pages/MDDashboard";
import FinanceDashboard from "./pages/FinanceDashboard";
import StoreDashboard from "./pages/StoreDashboard";
import ServiceDashboard from "./pages/ServiceDashboard";
import DigitalMarketingDashboard from "./pages/DigitalMarketingDashboard";
import HRDashboard from "./pages/HRDashboard";
import PublicDocPage from "./pages/PublicDocPage";
import PublicLinks from "./pages/PublicLinks";
import Vendors from "./pages/Vendors";
import PurchaseOrders from "./pages/PurchaseOrders";
import DeliveryChallans from "./pages/DeliveryChallans";
import AMCContracts from "./pages/AMCContracts";
import ServiceTasks from "./pages/ServiceTasks";
import HRStaff from "./pages/HRStaff";
import WhatsAppTemplates from "./pages/WhatsAppTemplates";
import WhatsAppDashboard from "./pages/WhatsAppDashboard";
import AdminHelpContent from "./pages/AdminHelpContent";
import WhatsappFallbackModal from "./components/WhatsappFallbackModal";
import GlobalSearchPalette from "./components/GlobalSearchPalette";
import { Toaster } from "./components/ui/sonner";
import { useAuth } from "./hooks/useAuth";
import { NavigationGuardProvider } from "./components/NavigationGuard";
import FollowUpAlertPopup from "./components/leads/FollowUpAlertPopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ProtectedRoute = ({ user, logout, children }) => {
  if (!user) return <Navigate to="/auth" />;
  return typeof children === 'function' ? children({ user, logout }) : children;
};

function App() {
  const { user, loading, login, logout } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" />
      <WhatsappFallbackModal />
      <BrowserRouter>
        {user && <FollowUpAlertPopup user={user} />}
        <NavigationGuardProvider>
          <GlobalSearchPalette user={user} />
          <Routes>
          <Route path="/" element={<Landing user={user} logout={logout} />} />
          <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <Auth login={login} />} />
          {/* Public document pages — no auth required */}
          <Route path="/public/:docType/:token" element={<PublicDocPage />} />
          <Route path="/dealer-register" element={<DealerRegister />} />
          <Route path="/admin/dealers" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AdminDealers {...p} />}</ProtectedRoute>} />
          <Route path="/quote/:token" element={<PublicQuote />} />
          <Route path="/dashboard" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Dashboard {...p} />}</ProtectedRoute>} />
          <Route path="/my-dashboard" element={<ProtectedRoute user={user} logout={logout}>{(p) => <RoleDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/approvals" element={<ProtectedRoute user={user} logout={logout}>{(p) => <ApprovalsCenter {...p} />}</ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute user={user} logout={logout}>{(p) => <HelpSupport {...p} />}</ProtectedRoute>} />
          <Route path="/products" element={<ProductCatalogue user={user} logout={logout} />} />
          <Route path="/products-admin" element={<Products user={user} logout={logout} />} />
          <Route path="/admin/catalogue" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AdminCatalogue {...p} />}</ProtectedRoute>} />
          <Route path="/products/:productId" element={<ProductDetail user={user} logout={logout} />} />
          <Route path="/campaigns" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Campaigns {...p} />}</ProtectedRoute>} />
          <Route path="/leads" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Leads {...p} />}</ProtectedRoute>} />
          <Route path="/leads/:leadId" element={<ProtectedRoute user={user} logout={logout}>{(p) => <LeadDetailPage {...p} />}</ProtectedRoute>} />
          <Route path="/crm/deals/:dealId" element={<ProtectedRoute user={user} logout={logout}>{(p) => <DealDetailPage {...p} />}</ProtectedRoute>} />
          <Route path="/billing/:docType/:docId" element={<ProtectedRoute user={user} logout={logout}>{(p) => <BillingDocPage {...p} />}</ProtectedRoute>} />
          <Route path="/social-media" element={<ProtectedRoute user={user} logout={logout}>{(p) => <SocialMedia {...p} />}</ProtectedRoute>} />
          <Route path="/email-marketing" element={<ProtectedRoute user={user} logout={logout}>{(p) => <EmailMarketing {...p} />}</ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Analytics {...p} />}</ProtectedRoute>} />
          <Route path="/crm" element={<ProtectedRoute user={user} logout={logout}>{(p) => <CRMDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/crm/contacts" element={<ProtectedRoute user={user} logout={logout}>{(p) => <CRMContacts {...p} />}</ProtectedRoute>} />
          <Route path="/crm/deals" element={<ProtectedRoute user={user} logout={logout}>{(p) => <CRMDeals {...p} />}</ProtectedRoute>} />
          <Route path="/crm/tasks" element={<ProtectedRoute user={user} logout={logout}>{(p) => <CRMTasks {...p} />}</ProtectedRoute>} />
          <Route path="/crm/activities" element={<ProtectedRoute user={user} logout={logout}>{(p) => <CRMActivities {...p} />}</ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Reports {...p} />}</ProtectedRoute>} />
          <Route path="/reports-hub" element={<ProtectedRoute user={user} logout={logout}>{(p) => <ReportsHub {...p} />}</ProtectedRoute>} />
          <Route path="/reports/pipeline-velocity" element={<ProtectedRoute user={user} logout={logout}>{(p) => <PipelineVelocityReport {...p} />}</ProtectedRoute>} />
          <Route path="/reports/bde-workload" element={<ProtectedRoute user={user} logout={logout}>{(p) => <BDEWorkloadReport {...p} />}</ProtectedRoute>} />
          <Route path="/reports/public-pdf-engagement" element={<ProtectedRoute user={user} logout={logout}>{(p) => <PublicPdfEngagementReport {...p} />}</ProtectedRoute>} />
          <Route path="/forecasting" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Forecasting {...p} />}</ProtectedRoute>} />
          <Route path="/crm/contacts/:contactId/360" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Contact360 {...p} />}</ProtectedRoute>} />
          <Route path="/team-performance" element={<ProtectedRoute user={user} logout={logout}>{(p) => <TeamPerformance {...p} />}</ProtectedRoute>} />
          <Route path="/lead-scoring" element={<ProtectedRoute user={user} logout={logout}>{(p) => <LeadScoring {...p} />}</ProtectedRoute>} />
          <Route path="/meetings" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Meetings {...p} />}</ProtectedRoute>} />
          <Route path="/sales-funnel" element={<ProtectedRoute user={user} logout={logout}>{(p) => <SalesFunnel {...p} />}</ProtectedRoute>} />
          <Route path="/social-leads" element={<ProtectedRoute user={user} logout={logout}>{(p) => <SocialLeads {...p} />}</ProtectedRoute>} />
          <Route path="/user-management" element={<ProtectedRoute user={user} logout={logout}>{(p) => <UserManagement {...p} />}</ProtectedRoute>} />
          <Route path="/promotions" element={<ProtectedRoute user={user} logout={logout}>{(p) => <PromotionsManager {...p} />}</ProtectedRoute>} />
          <Route path="/whatsapp" element={<ProtectedRoute user={user} logout={logout}>{(p) => <WhatsAppHub {...p} />}</ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute user={user} logout={logout}>{(p) => <BillingCenter {...p} />}</ProtectedRoute>} />
          <Route path="/incentives" element={<ProtectedRoute user={user} logout={logout}>{(p) => <IncentiveCenter {...p} />}</ProtectedRoute>} />
          <Route path="/incentive-srs" element={<ProtectedRoute user={user} logout={logout}>{(p) => <IncentiveSRS {...p} />}</ProtectedRoute>} />
          <Route path="/notes" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Notes {...p} />}</ProtectedRoute>} />
          <Route path="/alerts" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Alerts {...p} />}</ProtectedRoute>} />
          <Route path="/admin/enquiries" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AdminEnquiries {...p} />}</ProtectedRoute>} />
          <Route path="/admin/leads" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AdminProductLeads {...p} />}</ProtectedRoute>} />
          <Route path="/leads-dashboard" element={<ProtectedRoute user={user} logout={logout}>{(p) => <LeadsDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AdminSettings {...p} />}</ProtectedRoute>} />
          <Route path="/marketing-campaigns" element={<ProtectedRoute user={user} logout={logout}>{(p) => <MarketingCampaigns {...p} />}</ProtectedRoute>} />
          <Route path="/social-hub" element={<ProtectedRoute user={user} logout={logout}>{(p) => <SocialHub {...p} />}</ProtectedRoute>} />
          <Route path="/bde-dashboard" element={<ProtectedRoute user={user} logout={logout}>{(p) => <BDEDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/manager-dashboard" element={<ProtectedRoute user={user} logout={logout}>{(p) => <ManagerDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/admin/departments" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AdminDepartments {...p} />}</ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AdminRoles {...p} />}</ProtectedRoute>} />
          <Route path="/dashboard/md" element={<ProtectedRoute user={user} logout={logout}>{(p) => <MDDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/dashboard/finance" element={<ProtectedRoute user={user} logout={logout}>{(p) => <FinanceDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/dashboard/store" element={<ProtectedRoute user={user} logout={logout}>{(p) => <StoreDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/dashboard/service" element={<ProtectedRoute user={user} logout={logout}>{(p) => <ServiceDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/dashboard/digital-marketing" element={<ProtectedRoute user={user} logout={logout}>{(p) => <DigitalMarketingDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/dashboard/hr" element={<ProtectedRoute user={user} logout={logout}>{(p) => <HRDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/admin/public-links" element={<ProtectedRoute user={user} logout={logout}>{(p) => <PublicLinks {...p} />}</ProtectedRoute>} />
          {/* Phase 4b-2: Operations modules + WhatsApp Templates */}
          <Route path="/vendors" element={<ProtectedRoute user={user} logout={logout}>{(p) => <Vendors {...p} />}</ProtectedRoute>} />
          <Route path="/purchase-orders" element={<ProtectedRoute user={user} logout={logout}>{(p) => <PurchaseOrders {...p} />}</ProtectedRoute>} />
          <Route path="/delivery-challans" element={<ProtectedRoute user={user} logout={logout}>{(p) => <DeliveryChallans {...p} />}</ProtectedRoute>} />
          <Route path="/amc-contracts" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AMCContracts {...p} />}</ProtectedRoute>} />
          <Route path="/service-tasks" element={<ProtectedRoute user={user} logout={logout}>{(p) => <ServiceTasks {...p} />}</ProtectedRoute>} />
          <Route path="/hr-staff" element={<ProtectedRoute user={user} logout={logout}>{(p) => <HRStaff {...p} />}</ProtectedRoute>} />
          <Route path="/whatsapp-templates" element={<ProtectedRoute user={user} logout={logout}>{(p) => <WhatsAppTemplates {...p} />}</ProtectedRoute>} />
          <Route path="/whatsapp/dashboard" element={<ProtectedRoute user={user} logout={logout}>{(p) => <WhatsAppDashboard {...p} />}</ProtectedRoute>} />
          <Route path="/admin/help-content" element={<ProtectedRoute user={user} logout={logout}>{(p) => <AdminHelpContent {...p} />}</ProtectedRoute>} />
          </Routes>
        </NavigationGuardProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
